import { Trans, t } from "@lingui/macro"
import { useStore } from "effector-react"
import { array, nonEmptyArray, ord, record } from "fp-ts"
import { pipe } from "fp-ts/function"
import { AnimatePresence, motion } from "framer-motion"
import Link from "next/link"
import React, { MouseEventHandler, ReactNode } from "react"
import ArrowRightIcon from "remixicon-react/ArrowRightLineIcon"
import NotificationIcon from "remixicon-react/Notification4FillIcon"
import NotificationOffIcon from "remixicon-react/NotificationOffFillIcon"

import { useAuthorWatchdogState } from "@/domains/author-watchdogs/query"
import * as Author from "@/domains/authors"
import { useI18n } from "@/lib/i18n"
import { centuriesList } from "@/lib/messages"
import { QueryView } from "@/lib/query"
import { LocaleOrd } from "@/lib/string"
import { styled } from "@/stitches"
import {
  AspectRatioImage,
  ColorLayer,
  IconLayer,
  ImageProp,
} from "@/ui/aspect-ratio-image"
import { Box } from "@/ui/box"
import { IconButton } from "@/ui/button"
import { Container } from "@/ui/container"
import { Separator } from "@/ui/separator"
import { Skeleton } from "@/ui/skeleton"
import { Slider } from "@/ui/slider"
import { Stack } from "@/ui/stack"
import { Title } from "@/ui/text"
import { Text } from "@/ui/text"
import { Tooltip } from "@/ui/tooltip"

import { Layout } from "../_layout"
import * as FiltersStore from "./filters"
import { Filters } from "./filters"

const ALPHABET = nonEmptyArray
  .range(1, 26)
  .map((x) => String.fromCharCode(x + 64))

export function AlphabetBar({
  availableLetters,
}: {
  availableLetters: Array<string>
}) {
  const onLetterClick =
    (letter: string): MouseEventHandler<HTMLAnchorElement> =>
    (event) => {
      event.preventDefault()
      const element = document.querySelector(`#${letter}`)

      if (element) {
        scrollToElement(element)
      }
    }

  return (
    <Stack
      as="nav"
      wrap
      css={{
        mb: "$12",
        bg: "$gray1",
        position: "sticky",
        top: 0,
        zIndex: 20,
        mx: "-$3",
      }}
    >
      {ALPHABET.map((letter) => {
        const disabled = !availableLetters.includes(letter)
        return (
          <IconButton
            as="a"
            size="sm2"
            href={disabled ? undefined : `#${letter}`}
            key={letter}
            rect
            className={disabled ? "disabled" : ""}
            onClick={onLetterClick(letter)}
            css={{
              flexBasis: "2rem",
              "@md": {
                flexBasis: "2.75rem",
                flexShrink: 2,
              },
              "@xl": {
                flexBasis: "3rem",
              },
            }}
          >
            {letter}
          </IconButton>
        )
      })}
    </Stack>
  )
}

const AuthorsGrid = styled("ol", {
  display: "grid",
  columnGap: "$4",
  rowGap: "$6",
  "@sm": {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  "@md": {
    gridTemplateColumns: "repeat(3, 1fr)",
  },
  "@lg": {
    gridTemplateColumns: "repeat(4, 1fr)",
  },
})

export function AuthorItemTemplate(props: {
  link: string
  image?: ImageProp
  imageAlt: string
  name: string
  details?: ReactNode
  actions?: ReactNode
}) {
  return (
    <Box as="li" position="relative" className="item-group">
      <Link href={props.link} passHref>
        <Stack
          as="a"
          gap={4}
          align="center"
          css={{ mr: "$6" }}
          className="group"
        >
          <Box css={{ width: "6rem", flexShrink: 0 }}>
            <AspectRatioImage image={props.image} alt={props.imageAlt}>
              <ColorLayer />
              <IconLayer>
                <ArrowRightIcon direction="right" size="2em" />
              </IconLayer>
            </AspectRatioImage>
          </Box>
          <Stack direction="column" gap={1}>
            <Text as="h4" size="lg" weight="bold">
              {props.name}
            </Text>
            <Stack columnGap={2} wrap>
              {props.details}
            </Stack>
          </Stack>
        </Stack>
      </Link>
      {props.actions}
    </Box>
  )
}

export function AuthorsSectionTemplate(props: {
  id: string
  title: string
  children: ReactNode
}) {
  return (
    <Stack as="li" direction="column" gap={4}>
      <Text
        id={props.id}
        as="h3"
        size="2xl"
        weight="bold"
        css={{
          bg: "$gray1",
          position: "sticky",
          top: "6rem",
          zIndex: 10,
          "@md": {
            top: 48,
          },
        }}
      >
        {props.title}
      </Text>
      <AuthorsGrid>{props.children}</AuthorsGrid>
    </Stack>
  )
}

export function AuthorItem({ author }: { author: Author.AuthorPreview }) {
  const { localize } = useI18n()
  const watchdogState = useAuthorWatchdogState(author.id)

  return (
    <AuthorItemTemplate
      link={Author.hrefOf(author)}
      image={author.images[0]?.file}
      imageAlt={author.name}
      name={author.name}
      details={
        <>
          {author.country && (
            <Text as="p" variant="label">
              {localize(author.country.name)}
            </Text>
          )}
          <Separator orientation="vertical" />
          {author.centuries && (
            <Text as="p" variant="label">
              {centuriesList(author.centuries)}
            </Text>
          )}
        </>
      }
      actions={
        <AnimatePresence>
          {watchdogState.status === "success" && (
            <Box
              as={motion.div}
              css={{ position: "absolute", top: 0, right: 0 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Tooltip
                asChild
                content={
                  watchdogState.isOn
                    ? t({
                        id: "aurhorCard.unwatchAuthorButton.tooltip",
                        message: "Zrušit hlídání autora",
                      })
                    : t({
                        id: "aurhorCard.watchAuthorButton.tooltip",
                        message: "Hlídat autora",
                      })
                }
              >
                <IconButton
                  color="secondary"
                  size="sm"
                  onClick={watchdogState.toggle}
                  active={watchdogState.isOn}
                  css={
                    watchdogState.isOn
                      ? {
                          ".off-icon": { display: "none" },
                          "&:hover": {
                            ".off-icon": { display: "inline" },
                            ".on-icon": { display: "none" },
                          },
                        }
                      : {
                          opacity: 0,
                          ".item-group:hover &": {
                            opacity: 1,
                          },
                          ".off-icon": { display: "none" },
                        }
                  }
                >
                  <NotificationIcon className="on-icon" size="1em" />
                  <NotificationOffIcon className="off-icon" size="1em" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </AnimatePresence>
      }
    />
  )
}

export function AuthorListSkeleton() {
  return (
    <AuthorsGrid>
      {nonEmptyArray.range(1, 6).map((index) => (
        <Skeleton key={index} css={{ height: 160 }} />
      ))}
    </AuthorsGrid>
  )
}

function scrollToElement(element: Element) {
  const headerOffset = 80
  const elementPosition = element.getBoundingClientRect().top
  const offsetPosition = elementPosition + window.pageYOffset - headerOffset

  window.scrollTo({
    top: offsetPosition,
    behavior: "smooth",
  })
}

const byGroupLetter = pipe(
  LocaleOrd,
  ord.contramap(([letter]: [string, unknown]) => letter)
)

function AuthorGroupsList({
  groups,
}: {
  groups: Array<[string, Array<Author.AuthorPreview>]>
}) {
  if (array.isEmpty(groups)) {
    return (
      <Text size="lg" color="muted">
        <Trans id="authorList.noAuthorsSatisfyingSpecifiedFilters">
          Filtrům nedopovídají žádní autoři
        </Trans>
      </Text>
    )
  }

  return (
    <Stack as="ol" direction="column" gap={8}>
      {groups.map(([letter, authors]) => (
        <AuthorsSectionTemplate key={letter} id={letter} title={letter}>
          {authors.map((author) => (
            <AuthorItem key={author.id} author={author} />
          ))}
        </AuthorsSectionTemplate>
      ))}
      <AuthorsSectionTemplate id="unknown" title={t`author.unknown`}>
        <AuthorItemTemplate
          link="/authors/unknown"
          imageAlt={t`author.unknown`}
          name={t`author.unknown`}
        />
      </AuthorsSectionTemplate>
    </Stack>
  )
}

export default function AuthorListPage() {
  const filters = useStore(FiltersStore.$filters)
  const currentAuctionBlockAuthorsQuery = Author.useAuthorListQuery({
    currentAuctionBlock: true,
  })
  const authorsQuery = Author.useAuthorListQuery({
    country: filters.country?.id,
    century: filters.century,
  })

  const authorGroupsByLetter = pipe(
    authorsQuery.data ?? [],
    array.sort(Author.byName),
    (authors) =>
      array.isNonEmpty(authors)
        ? pipe(
            authors,
            nonEmptyArray.groupBy(
              (author) => author.surname[0]?.toLocaleUpperCase() ?? ""
            )
          )
        : {},
    record.toArray,
    array.sort(byGroupLetter)
  )

  const availableLetters = authorGroupsByLetter.map(([letter]) => letter)

  return (
    <Layout>
      <Box as="section" css={{ mt: "$8", mb: "$12" }}>
        <Container css={{ mb: "$8" }}>
          <Title as="h2">
            <Trans id="authorList.currentAuctionAuthors.title">
              Autoři v aktuální aukci
            </Trans>
          </Title>
        </Container>
        <QueryView
          query={currentAuctionBlockAuthorsQuery}
          loading={
            <Slider role="presentation">
              <Stack as="ul" gap={6}>
                {nonEmptyArray.range(1, 4).map((index) => (
                  <Skeleton
                    as="li"
                    key={index}
                    css={{
                      width: "18rem",
                      height: "24rem",
                      flexShrink: 0,
                    }}
                  />
                ))}
              </Stack>
            </Slider>
          }
          success={(authors) => (
            <Slider>
              <Stack as="ul" gap={6}>
                {authors.map((author) => (
                  <li key={author.id}>
                    <Author.AuthorCard author={author} />
                  </li>
                ))}
              </Stack>
            </Slider>
          )}
        />
      </Box>
      <Container css={{ mb: "$12" }}>
        <Box as="section">
          <Title as="h2" css={{ mb: "$8" }}>
            <Trans id="authorList.allAuthors.title">Všichni autoři</Trans>
          </Title>
          <Filters
            disabled={!authorsQuery.data}
            loading={authorsQuery.isFetching && authorsQuery.isPreviousData}
          />
          <AlphabetBar availableLetters={availableLetters} />
          {authorsQuery.data ? (
            <AuthorGroupsList groups={authorGroupsByLetter} />
          ) : (
            <AuthorListSkeleton />
          )}
        </Box>
      </Container>
    </Layout>
  )
}
